import { useMemo, useState } from "react";
import { Pencil, Trash2, Search } from "lucide-react";
import { Modal } from "@/shared/components/Modal";
import { ModalConfirmacao } from "@/shared/components/ModalConfirmacao";
import { ModalSairSemSalvar } from "@/shared/components/ModalSairSemSalvar";
import { Button } from "@/shared/components/Button";
import { Paginacao } from "@/shared/components/Paginacao";
import { useAuth } from "@/core/hooks/useAuth";
import { FormularioLivro, type FormularioLivroValores } from "@/modules/biblioteca/components/FormularioLivro";
import { DropdownCategoriaPesquisavel } from "@/modules/biblioteca/components/DropdownCategoriaPesquisavel";
import { useLivros, useCriarLivro, useAtualizarLivro, useExcluirLivro } from "@/modules/biblioteca/hooks/useLivros";
import type { LivroComCategoria } from "@/modules/biblioteca/services/livrosService";
import { formatarNumeroCadastro, numeroCorrespondeBusca } from "@/modules/biblioteca/utils/numeroCadastro";

const ITENS_POR_PAGINA = 10;

/** Campo em branco = livro sem número de cadastro. */
function converterNumeroCadastro(valor: string | undefined): number | null {
  return valor ? Number(valor) : null;
}

export function GerenciarLivros() {
  // UX-01 (auditoria 20/07/2026): esta tela não escondia os botões de
  // editar/excluir de ninguém — quem chegasse pela rota (admin incluído,
  // mesmo sem fazer parte do ministério Biblioteca) via os botões, e a
  // RLS ("Admin gerencia livros" exige gerencia_modulo('biblioteca'), sem
  // bypass de admin) rejeitava a ação na hora de salvar. Agora os botões só
  // aparecem para quem realmente gerencia o módulo.
  const { podeGerenciarBiblioteca } = useAuth();
  const { data: livros, isLoading } = useLivros();
  const criarLivro = useCriarLivro();
  const atualizarLivro = useAtualizarLivro();
  const excluirLivro = useExcluirLivro();

  const [modalAberto, setModalAberto] = useState(false);
  const [livroEmEdicao, setLivroEmEdicao] = useState<LivroComCategoria | null>(null);
  const [filtroCategoriaId, setFiltroCategoriaId] = useState("todas");
  const [erroExclusao, setErroExclusao] = useState<string | null>(null);
  const [pesquisa, setPesquisa] = useState("");
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [livroParaExcluir, setLivroParaExcluir] = useState<LivroComCategoria | null>(null);
  const [erroFormulario, setErroFormulario] = useState<string | null>(null);
  const [formularioSujo, setFormularioSujo] = useState(false);
  const [confirmandoFechamento, setConfirmandoFechamento] = useState(false);

  // UX-06: um livro pode ter mais de uma categoria agora — a lista de
  // categorias presentes e o filtro consideram todas as categorias de cada
  // livro, não só uma.
  const categoriasPresentes = useMemo(() => {
    const mapa = new Map<string, string>();
    (livros ?? []).forEach((l) => {
      l.categorias.forEach((c) => mapa.set(c.id, c.nome));
    });
    return Array.from(mapa.entries())
      .map(([id, nome]) => ({ id, nome }))
      .sort((a, b) => a.nome.localeCompare(b.nome));
  }, [livros]);

  const livrosFiltrados = useMemo(() => {
    let lista = livros;
    if (!lista) return lista;
    if (filtroCategoriaId !== "todas")
      lista = lista.filter((l) => l.categorias.some((c) => c.id === filtroCategoriaId));
    const termo = pesquisa.trim().toLowerCase();
    if (termo)
      lista = lista.filter(
        (l) =>
          l.nome_livro.toLowerCase().includes(termo) ||
          (l.autor ?? "").toLowerCase().includes(termo) ||
          numeroCorrespondeBusca(l.numero_cadastro, termo),
      );
    return lista;
  }, [livros, filtroCategoriaId, pesquisa]);

  // Resumo para quem gerencia a biblioteca: quantos títulos estão cadastrados
  // (e quantos aparecem com a pesquisa/categoria atual) e o total de
  // exemplares. Considera todos os livros, inclusive os marcados como
  // inativos.
  const totalTitulos = livros?.length ?? 0;
  const totalExemplares = useMemo(
    () => (livros ?? []).reduce((soma, l) => soma + l.quantidade_total, 0),
    [livros],
  );
  const filtrando = filtroCategoriaId !== "todas" || pesquisa.trim() !== "";

  const chaveFiltros = `${filtroCategoriaId}-${pesquisa}`;
  const [chaveFiltrosAnterior, setChaveFiltrosAnterior] = useState(chaveFiltros);
  if (chaveFiltros !== chaveFiltrosAnterior) {
    setChaveFiltrosAnterior(chaveFiltros);
    setPaginaAtual(1);
  }

  const totalPaginas = Math.max(1, Math.ceil((livrosFiltrados?.length ?? 0) / ITENS_POR_PAGINA));
  const livrosPaginados = useMemo(() => {
    if (!livrosFiltrados) return livrosFiltrados;
    const inicio = (paginaAtual - 1) * ITENS_POR_PAGINA;
    return livrosFiltrados.slice(inicio, inicio + ITENS_POR_PAGINA);
  }, [livrosFiltrados, paginaAtual]);

  function abrirCriacao() { setLivroEmEdicao(null); setErroFormulario(null); setFormularioSujo(false); setModalAberto(true); }
  function abrirEdicao(livro: LivroComCategoria) { setLivroEmEdicao(livro); setErroFormulario(null); setFormularioSujo(false); setModalAberto(true); }

  function tentarFecharModal() {
    if (formularioSujo) {
      setConfirmandoFechamento(true);
    } else {
      fecharModalDeVerdade();
    }
  }

  function fecharModalDeVerdade() {
    setModalAberto(false);
    setErroFormulario(null);
    setFormularioSujo(false);
    setConfirmandoFechamento(false);
  }

  async function handleSubmit(dados: FormularioLivroValores) {
    setErroFormulario(null);
    try {
      if (livroEmEdicao) {
        await atualizarLivro.mutateAsync({ id: livroEmEdicao.id, dados: { nomeLivro: dados.nomeLivro, isbn: dados.isbn, autor: dados.autor, categoriaIds: dados.categoriaIds, numeroCadastro: converterNumeroCadastro(dados.numeroCadastro), quantidadeTotal: dados.quantidadeTotal, descricao: dados.descricao, status: dados.status }, livroAtual: livroEmEdicao });
      } else {
        await criarLivro.mutateAsync({ nomeLivro: dados.nomeLivro, isbn: dados.isbn, autor: dados.autor, categoriaIds: dados.categoriaIds, numeroCadastro: converterNumeroCadastro(dados.numeroCadastro), quantidadeTotal: dados.quantidadeTotal, descricao: dados.descricao, status: dados.status });
      }
      setModalAberto(false);
      setFormularioSujo(false);
    } catch (erroSubmit) {
      setErroFormulario(erroSubmit instanceof Error ? erroSubmit.message : "Não foi possível salvar o livro.");
    }
  }

  async function confirmarExclusao() {
    if (!livroParaExcluir) return;
    setErroExclusao(null);
    try {
      await excluirLivro.mutateAsync(livroParaExcluir.id);
      setLivroParaExcluir(null);
    } catch (erro) {
      setLivroParaExcluir(null);
      setErroExclusao(erro instanceof Error ? erro.message : "Não foi possível excluir o livro.");
    }
  }

  return (
    <>
      <main className="mx-auto w-full max-w-4xl px-4 py-8 sm:px-6 sm:py-10">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-2xl font-bold text-brand-verde">Acervo de livros</h1>
          {podeGerenciarBiblioteca && (
            <Button onClick={abrirCriacao} className="bg-brand-verde text-white">+ Novo livro</Button>
          )}
        </div>

        <div className="relative mt-4">
          <Search size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-muted" />
          <input
            type="search"
            placeholder="Pesquisar por título, autor ou nº..."
            value={pesquisa}
            onChange={(e) => setPesquisa(e.target.value)}
            className="w-full rounded-xl border border-border bg-surface py-3 pl-11 pr-4 text-sm text-text placeholder:text-muted focus:border-brand-verde focus:outline-none focus:ring-1 focus:ring-brand-verde"
          />
        </div>

        {!!categoriasPresentes.length && (
          <div className="mt-3">
            <DropdownCategoriaPesquisavel
              categorias={categoriasPresentes}
              valorSelecionado={filtroCategoriaId}
              onSelecionar={setFiltroCategoriaId}
            />
          </div>
        )}

        {podeGerenciarBiblioteca && !isLoading && (
          <p className="mt-4 text-sm text-muted" aria-live="polite">
            {filtrando ? (
              <>
                <span className="font-semibold text-text">{livrosFiltrados?.length ?? 0}</span> de{" "}
                {totalTitulos} {totalTitulos === 1 ? "livro cadastrado" : "livros cadastrados"}
              </>
            ) : (
              <>
                <span className="font-semibold text-text">{totalTitulos}</span>{" "}
                {totalTitulos === 1 ? "livro cadastrado" : "livros cadastrados"}
                {" · "}
                <span className="font-semibold text-text">{totalExemplares}</span>{" "}
                {totalExemplares === 1 ? "exemplar" : "exemplares"} no total
              </>
            )}
          </p>
        )}

        {erroExclusao && (
          <p role="alert" className="mt-4 rounded-md bg-danger/10 px-4 py-3 text-sm text-danger">{erroExclusao}</p>
        )}

        <div className="mt-4 overflow-hidden rounded-xl border border-border bg-surface">
          {isLoading ? (
            <p className="p-6 text-sm text-muted">Carregando...</p>
          ) : !livrosFiltrados?.length ? (
            <p className="p-6 text-sm text-muted">Nenhum livro cadastrado.</p>
          ) : (
            <ul>
              {livrosPaginados?.map((livro, idx) => (
                <li key={livro.id} className={`flex items-center gap-3 px-4 py-3 ${idx > 0 ? "border-t border-border" : ""}`}>
                  {/* Etiqueta com o número de cadastro (o da lombada do livro físico). Livro sem
                      número mostra a etiqueta vazia para a lista não desalinhar. */}
                  <div
                    className="flex w-14 shrink-0 flex-col items-center rounded-lg border border-border bg-surface-2 py-1.5 text-center"
                    aria-label={livro.numero_cadastro != null ? `Número de cadastro ${formatarNumeroCadastro(livro.numero_cadastro)}` : "Sem número de cadastro"}
                  >
                    <span className="text-[9px] font-bold uppercase tracking-wider text-muted">Nº</span>
                    {livro.numero_cadastro != null ? (
                      <span className="font-mono text-base font-bold tabular-nums text-text">{formatarNumeroCadastro(livro.numero_cadastro)}</span>
                    ) : (
                      <span className="font-mono text-base font-bold text-muted">—</span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-text">{livro.nome_livro}</p>
                    <p className="mt-0.5 text-xs text-muted">
                      {livro.autor && <span>{livro.autor} · </span>}
                      {livro.categorias_nomes && <span>{livro.categorias_nomes} · </span>}
                      <span>{livro.quantidade_disponivel}/{livro.quantidade_total} disp. · </span>
                      <span className={`inline-block rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${livro.status === "Disponivel" ? "bg-success/10 text-success" : "bg-surface-2 text-muted"}`}>
                        {livro.status === "Disponivel" ? "Ativo" : "Inativo"}
                      </span>
                    </p>
                  </div>
                  {podeGerenciarBiblioteca && (
                    <div className="flex shrink-0 items-center gap-1">
                      <button onClick={() => abrirEdicao(livro)} aria-label="Editar" className="rounded-lg p-2 text-info transition-colors hover:bg-info/10">
                        <Pencil size={16} />
                      </button>
                      <button onClick={() => setLivroParaExcluir(livro)} aria-label="Excluir" className="rounded-lg p-2 text-danger transition-colors hover:bg-danger/10">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <Paginacao paginaAtual={paginaAtual} totalPaginas={totalPaginas} onMudarPagina={setPaginaAtual} />
      </main>

      <Modal titulo={livroEmEdicao ? "Editar livro" : "Novo livro"} aberto={modalAberto} onClose={tentarFecharModal} protegerSaida={false} carregando={criarLivro.isPending || atualizarLivro.isPending}>
        <FormularioLivro
          livroEmEdicao={livroEmEdicao}
          carregando={criarLivro.isPending || atualizarLivro.isPending}
          erro={erroFormulario}
          onDirtyChange={setFormularioSujo}
          onSubmit={handleSubmit}
        />
      </Modal>

      <ModalConfirmacao
        aberto={!!livroParaExcluir}
        titulo="Excluir livro"
        mensagem={`Tem certeza que deseja excluir "${livroParaExcluir?.nome_livro}"? Esta ação não pode ser desfeita.`}
        textoBotaoConfirmar="Excluir livro"
        carregando={excluirLivro.isPending}
        onConfirmar={confirmarExclusao}
        onCancelar={() => setLivroParaExcluir(null)}
      />

      <ModalSairSemSalvar
        aberto={confirmandoFechamento}
        onSair={fecharModalDeVerdade}
        onContinuar={() => setConfirmandoFechamento(false)}
      />
    </>
  );
}
